import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import type {
  ReviewAccountItem,
  ReviewAccountStatus,
  UpdateReviewAccountBody,
  UpdateReviewAccountResult,
} from '@sparkapp/shared-types';
import { Card } from '@/components/ui/card';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import AddReviewAccountForm from '@/components/review-accounts/AddReviewAccountForm';
import ReviewAccountConfirmModal, {
  type ReviewAccountConfirmAction,
} from '@/components/review-accounts/ReviewAccountConfirmModal';
import { useRemoveReviewAccount, useReviewAccounts, useUpdateReviewAccount } from '@/hooks/useReviewAccounts';

const COLUMNS = ['Email', 'Platform', 'Status', 'Window ends', 'Sessions', 'Last changed', 'Actions'];

const STATUS_BADGE: Record<ReviewAccountStatus, { label: string; variant: BadgeProps['variant'] }> = {
  active: { label: 'Active', variant: 'default' },
  disabled: { label: 'Disabled', variant: 'outline' },
  expired: { label: 'Expired', variant: 'destructive' },
};

const PLATFORM_LABEL = { ios: 'iOS', android: 'Android' } as const;

const SECONDARY_BUTTON =
  'px-3 py-1.5 text-sm font-semibold rounded-pine-input border-[1.5px] border-pine-line text-pine-ink-soft hover:bg-pine-hover-button transition-colors disabled:opacity-50 disabled:pointer-events-none';
const DANGER_BUTTON =
  'px-3 py-1.5 text-sm font-semibold rounded-pine-input border-[1.5px] border-pine-danger text-pine-danger hover:bg-pine-danger hover:text-white transition-colors disabled:opacity-50 disabled:pointer-events-none';

const formatLocal = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** ISO → the local "YYYY-MM-DDTHH:mm" a datetime-local input expects. */
function toLocalInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const sessionsSuffix = (n: number) => (n > 0 ? `, ${n} session(s) signed out` : '');

type PendingConfirm = {
  action: ReviewAccountConfirmAction;
  account: ReviewAccountItem;
  /** The update to run on confirm; absent for remove */
  body?: UpdateReviewAccountBody;
};

interface ReviewAccountRowProps {
  account: ReviewAccountItem;
  busy: boolean;
  onUpdate: (account: ReviewAccountItem, body: UpdateReviewAccountBody) => void;
  onConfirm: (pending: PendingConfirm) => void;
}

function ReviewAccountRow({ account, busy, onUpdate, onConfirm }: ReviewAccountRowProps) {
  const { email } = account;
  const [windowEnd, setWindowEnd] = useState(() => toLocalInputValue(account.expiresAt));

  const saveExpiry = () => {
    if (!windowEnd) return;
    const expiresAt = new Date(windowEnd);
    const body = { expiresAt: expiresAt.toISOString() };
    // A past end closes the window and signs the reviewer out
    if (expiresAt <= new Date()) onConfirm({ action: 'close', account, body });
    else onUpdate(account, body);
  };

  const badge = STATUS_BADGE[account.status];

  return (
    <tr data-testid={`review-account-row-${email}`} className="border-b border-pine-line align-top">
      <td className="px-6 py-4 text-sm">
        <div>{email}</div>
        {account.nickname && <div className="text-pine-ink-soft">{account.nickname}</div>}
      </td>
      <td className="px-6 py-4 text-sm">{PLATFORM_LABEL[account.platform]}</td>
      <td className="px-6 py-4 text-sm">
        <Badge variant={badge.variant}>{badge.label}</Badge>
      </td>
      <td className="px-6 py-4 text-sm">{account.expiresAt ? formatLocal(account.expiresAt) : 'Open-ended'}</td>
      <td className="px-6 py-4 text-sm">{account.activeSessionCount}</td>
      <td className="px-6 py-4 text-sm">{formatLocal(account.updatedAt)}</td>
      <td className="px-6 py-4 text-sm">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <input
              type="datetime-local"
              data-testid={`review-account-expiry-${email}`}
              value={windowEnd}
              onChange={(e) => setWindowEnd(e.target.value)}
              className="h-9 px-2 rounded-md border border-pine-line bg-white text-pine-ink text-sm"
            />
            <button
              data-testid={`review-account-save-expiry-${email}`}
              disabled={busy || !windowEnd}
              onClick={saveExpiry}
              className={SECONDARY_BUTTON}
            >
              Save
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {account.expiresAt && (
              <button
                data-testid={`review-account-open-ended-${email}`}
                disabled={busy}
                onClick={() => onUpdate(account, { expiresAt: null })}
                className={SECONDARY_BUTTON}
              >
                Make open-ended
              </button>
            )}
            {account.enabled ? (
              <button
                data-testid={`review-account-disable-${email}`}
                disabled={busy}
                onClick={() => onConfirm({ action: 'close', account, body: { enabled: false } })}
                className={SECONDARY_BUTTON}
              >
                Disable
              </button>
            ) : (
              <button
                data-testid={`review-account-enable-${email}`}
                disabled={busy}
                onClick={() => onUpdate(account, { enabled: true })}
                className={SECONDARY_BUTTON}
              >
                Enable
              </button>
            )}
            <button
              data-testid={`review-account-remove-${email}`}
              disabled={busy}
              onClick={() => onConfirm({ action: 'remove', account })}
              className={DANGER_BUTTON}
            >
              Remove
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function ReviewAccountsPage() {
  const { data, isLoading, isError, error } = useReviewAccounts();
  const updateAccount = useUpdateReviewAccount();
  const removeAccount = useRemoveReviewAccount();
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const busy = updateAccount.isPending || removeAccount.isPending;

  const runUpdate = (account: ReviewAccountItem, body: UpdateReviewAccountBody, onDone?: () => void) => {
    updateAccount.mutate(
      { userId: account.userId, body },
      {
        onSuccess: ({ account: updated, revokedSessions }: UpdateReviewAccountResult) => {
          if (body.enabled === true && updated.status === 'expired') {
            toast.warning('Enabled, but the window has ended. Set a new end or make it open-ended.');
          } else {
            toast.success(`Updated ${updated.email}: ${updated.status}${sessionsSuffix(revokedSessions)}`);
          }
          onDone?.();
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  // Modal stays open on error so the admin can retry or cancel.
  const handleConfirm = () => {
    if (!pending) return;
    const { action, account, body } = pending;
    if (action === 'remove') {
      removeAccount.mutate(account.userId, {
        onSuccess: (result) => {
          toast.success(`Removed ${result.email}${sessionsSuffix(result.revokedSessions)}`);
          setPending(null);
        },
        onError: (e) => toast.error(e.message),
      });
    } else if (body) {
      runUpdate(account, body, () => setPending(null));
    }
  };

  useEffect(() => {
    document.title = 'Review Accounts | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (isError && error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load review accounts');
    }
  }, [isError, error]);

  return (
    <div className="space-y-6" data-testid="review-accounts-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div>
          <h1 className="text-pine-display font-display font-semibold text-pine-ink">Review Accounts</h1>
          <p className="text-pine-body text-pine-ink-soft mt-1">
            Reviewers sign in by typing their email. Open a window before submitting a build and close it
            when review is done. Disabling or removing signs the reviewer out.
          </p>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <AddReviewAccountForm />
      </motion.div>

      {isError && (
        <div className="admin-card p-6 border border-pine-danger/20 bg-pine-danger/5">
          <p className="text-pine-danger">Failed to load review accounts. Please try again.</p>
        </div>
      )}

      {!isError && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-pine-line">
                  {COLUMNS.map((label) => (
                    <th key={label} className="text-left px-6 py-4 text-sm font-medium text-pine-ink-soft">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 3 }, (_, i) => (
                    <tr key={i} className="border-b border-pine-line">
                      <td colSpan={COLUMNS.length} className="px-6 py-4">
                        <Skeleton className="h-5 w-full" />
                      </td>
                    </tr>
                  ))
                ) : data && data.accounts.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-6 py-8 text-center text-sm text-pine-ink-soft">
                      No reviewer accounts yet. Add one above.
                    </td>
                  </tr>
                ) : (
                  data?.accounts.map((account) => (
                    <ReviewAccountRow
                      // Remount on a server-side expiry change so the input picks it up
                      key={`${account.userId}:${account.expiresAt}`}
                      account={account}
                      busy={busy}
                      onUpdate={(a, body) => runUpdate(a, body)}
                      onConfirm={setPending}
                    />
                  ))
                )}
              </tbody>
            </table>
          </Card>
        </motion.div>
      )}

      <ReviewAccountConfirmModal
        open={pending !== null}
        action={pending?.action ?? 'close'}
        email={pending?.account.email ?? ''}
        activeSessionCount={pending?.account.activeSessionCount ?? 0}
        onClose={() => setPending(null)}
        onConfirm={handleConfirm}
        isLoading={busy}
      />
    </div>
  );
}
